import { Injectable } from '@angular/core';
import { GridData, GridDetails, GridHeader } from '../../tracking-overview/interfaces/grid';
import { ReportGridHeader, ActionType } from '../interfaces/report-grid';
import { ReportData } from '../interfaces/report-data';
import * as moment from 'moment';
import { DateRangeFilterValue, RangeKind } from '../../../shared/time-frame-filter/timeframe-filter.component';
import { ReportType, DateRangeFilter, SelectData, SelectItem, DateRangeTypes, DateRangeOption } from '../interfaces/filter-data';
import { DynamicListViewColumnData, UserConfigInfo } from '../../filter-samples/interface/dynamic-list-info';
import { SampleColumnsService } from '../../filter-samples/column/sample-column-service';
import { ColumnSettingInfo, SelectedColumns, AvailableColumns } from '../../../shared/covance-grid/column-settings/column-settings';
import { Observable } from 'rxjs';
import { ColumnSettingsService } from '../../../shared/covance-grid/column-settings/service/column-settings.service';
import { switchMap, map } from 'rxjs/operators';
import { SavedFilter } from '../interfaces/saved-filters';
import { ModalDialogService } from '../../../shared/model-dialog/modal-dialog.service';
import { DeleteModalData } from '../../../shared/model-dialog/delete/delete-modal.component';
import { ReportsStorageService } from './reports-storage-service';
import { ColumnInfo } from '../../filter-samples/filter-samples-info';

@Injectable()
export class ReportColumnsService {

    public generatedReportsHeader: ReportGridHeader[] = [
        {
            displayText: 'Report Name',
            key: 'generatedReportName',
            sort: 'asc',
            enableFilter: true,
            enableSearch: true,
            isLink: true,
        },
        {
            displayText: 'Report Type',
            key: 'generatedReportType',
            sort: null,
            enableFilter: true,
            enableSearch: true,
        },
        {
            displayText: 'Report ID',
            key: 'reportID',
            sort: null,
            enableFilter: true,
            enableSearch: true,
        },
        {
            displayText: 'Report Status',
            key: 'reportStatus',
            sort: null,
            enableFilter: true,
            enableSearch: true,
            displayCustomData: true,
            ngClass: [
                {
                    key: 'Queued',
                    class: 'status-progress'
                },
                {
                    key: 'In Progress',
                    class: 'status-progress'
                },
                {
                    key: 'Completed',
                    class: 'status-complete'
                },
                {
                    key: 'Too Large, Failed',
                    class: 'status-fail'
                }
            ]
        },
        {
            displayText: 'Record Count',
            key: 'recordCount',
            sort: null,
            enableFilter: true,
            enableSearch: true,
            defaultValue: '0'
        },
        {
            displayText: 'Completed Date Time',
            key: 'completedDateTime',
            sort: null,
            enableFilter: true,
            enableSearch: true,
            isDateTime: true,
        },
        {
            displayText: 'Study Grouping/ Protocol(s)',
            key: 'generatedReportContent',
            sort: null,
            formatData: true,
            enableFilter: true,
            enableSearch: true,
        },
        {
            displayText: 'Requested Date Time',
            key: 'requestedDateTime',
            sort: null,
            enableFilter: true,
            enableSearch: true,
            isDateTime: true,
        },
        {
            displayText: 'Download',
            key: null,
            sort: null,
            actions: [
                {
                    actionType: ActionType.exportExcel
                },
                {
                    actionType: ActionType.exportCsv
                },
            ]
        },
    ];

    public scheduledReportsHeader: ReportGridHeader[] = [
        {
            displayText: 'Report Name',
            key: 'scheduledReportName',
            sort: 'asc',
            enableFilter: true,
            enableSearch: true,
            isLink: true
        },
        {
            displayText: 'Report Type',
            key: 'scheduledReportType',
            sort: null,
            enableFilter: true,
            enableSearch: true,
        },
        {
            displayText: 'Study Grouping/ Protocol(s)',
            key: 'scheduledReportContent',
            formatData: true,
            sort: null,
            enableFilter: true,
            enableSearch: true,
        },
        {
            displayText: 'Next Report Scheduled Date Time',
            key: 'nextScheduledDateTime',
            sort: null,
            enableFilter: true,
            enableSearch: true,
            isDateTime: true,
        },
        {
            displayText: 'Schedule Pattern',
            key: 'reoccurencePattern',
            sort: null,
            enableFilter: true,
            enableSearch: true,
        },
        {
            displayText: 'Actions',
            key: null,
            sort: null,
            actions: [
                {
                    actionName: 'Edit',
                    description: 'Click to edit report',
                    actionType: ActionType.edit
                },
                {
                    actionName: 'Enable',
                    description: 'Turn On, if you want to schedule the report',
                    actionType: ActionType.enable
                },
                {
                    actionName: 'Notify',
                    description: 'Turn On, if you want to receive a notification via email',
                    actionType: ActionType.notify
                },
                {
                    actionName: 'Run',
                    description: 'Press Run if you want to Generate this Report now',
                    actionType: ActionType.run
                },
                {
                    actionName: 'Delete',
                    description: 'Click to delete this report',
                    actionType: ActionType.delete
                },
            ]
        },
    ];

    public reportDateRangeOptions: DateRangeFilter[] = [
        {
            key: 1,
            value: 'specimencollectedon',
            label: 'Visit Collection Date',
            isChecked: false
        },
        {
            key: 2,
            value: 'kit_received_on',
            label: 'Kit Receipt Date',
            isChecked: false
        },
        {
            key: 3,
            value: 'sample_received_on',
            label: 'Sample Receipt Date',
            isChecked: false
        }
    ];

    public sampleTrackingReportDateRangeOptions: DateRangeFilter[] = [
        ...this.reportDateRangeOptions,
        {
            key: 4,
            value: 'shippedon',
            label: 'Date Samples Shipped from Labcorp',
            isChecked: false
        }
    ];

    public customRangeKind: RangeKind = {
        key: 'dateRange', value: 'MM/DD/YY - Current', selected: false
    };

    constructor(
        public sampleColumnService: SampleColumnsService,
        public columnSettingsService: ColumnSettingsService,
        public reportStorage: ReportsStorageService,
        public deleteService: ModalDialogService) {

    }

    public getDateRangeFilters(): RangeKind[] {
        return [
            { key: 'weekly', value: 'Last 7 days', selected: false },
            { key: 'monthly', value: 'Last 30 days', selected: false },
            { key: 'twoMonths', value: 'Last 60 days', selected: false },
            { key: 'quarterly', value: 'Last 90 days', selected: false },
        ];
    }

    public getGeneratedReportColumns(): ReportGridHeader[] {
        return [
            {
                displayText: '',
                key: '',
                defaultValue: ' ',
                selectId: 'reportID',
                selectRow: true
            },
            ...this.generatedReportsHeader
        ];
    }

    public getDateRangeOptions(reportType: number) {
        if (reportType === 0) { return null; }
        return reportType === ReportType.SpecimenManagementInventoryReport ? this.reportDateRangeOptions :
                              this.sampleTrackingReportDateRangeOptions;
    }

    public getFilterData(selectedData: SelectData, selectedItemsList: SelectItem[]) {
        selectedData = {
            ...selectedData,
            selectedItems: selectedItemsList.
                map((id: any) => {
                    return (selectedData.listItems.find(li => li.id === id));
                }).filter((s: SelectItem) => s)
        };
        return selectedData;
    }

    public getDropDownValueList(title: string, itemList: SelectItem[]) {
        itemList = itemList.sort((a, b) => a.name.localeCompare(b.name));
        return {
            title: title,
            listItems: [
                ...new Map(
                    itemList.map<[string, SelectItem]>(item => [item.name, item]))
                    .values()
            ].filter(li => li.id),
        };
    }

    public applySearchToGrid(term: string, gridData: ReportData[], headers: ReportGridHeader[]) {
        if (!term || !gridData) {
            return { header: headers, data: gridData } as GridDetails;
        }
        term = term.trim().toLowerCase();
        let filterData: ReportData[] = [];
        for (var rowData of gridData) {
            for (var col of headers) {
                if (`${rowData[col.key as keyof ReportData]}`.toLowerCase().indexOf(term) !== -1) {
                    filterData.push(rowData);
                    break;
                }
            }
        }
        return { header: headers, data: filterData };
    }

    public getTodayRange(): DateRangeFilterValue {
        return {
            dateBegin: moment().format('YYYY-MM-DD'),
            dateEnd: moment().format('YYYY-MM-DD'),
            rangeType: 'today'
        };
    }

    public getTwoMonthsRange(): DateRangeFilterValue {
        return {
            dateBegin: moment().subtract(2, 'months').format('YYYY-MM-DD'),
            dateEnd: moment().format('YYYY-MM-DD'),
            rangeType: 'twoMonths'
        };
    }

    public getDateRangeValue(dateValue: DateRangeFilterValue, dateType?: string): any {
        if (!dateValue) { return ''; }
        if (dateValue.rangeType === 'dateRange') {
            let date = moment(dateType === 'end' ? dateValue.dateEnd : dateValue.dateBegin).format('MM-DD-YYYY');
            return date + ' ' + `${moment().local().format('hh:mm:ss Z')}`;
        }
        if (dateValue.rangeType === 'weekly') {
            return 7;
        }
        if (dateValue.rangeType === 'monthly') {
            return 30;
        }
        if (dateValue.rangeType === 'twoMonths') {
            return 60;
        }
        return 90;
    }

    public getSelectedDateRangeValue(dateRange: DateRangeOption): DateRangeFilterValue {
        return {
            dateBegin: moment(dateRange.fromDate).format('MM/DD/YYYY'),
            dateEnd: dateRange.toDate ? moment(dateRange.toDate).format('MM/DD/YYYY') : moment().format('MM/DD/YYYY'),
            rangeType: 'dateRange'
        };
    }

    public getSelectedDateRangeByDaysBack(daysBack: number): DateRangeFilterValue {
        if (daysBack === 7) {
            return {
                dateBegin: moment().subtract(7, 'd').format('MM/DD/YYYY'),
                dateEnd: moment().format('MM/DD/YYYY'),
                rangeType: 'weekly'
            };
        }
        if (daysBack === 30) {
            return {
                dateBegin: moment().subtract(30, 'd').format('MM/DD/YYYY'),
                dateEnd: moment().format('MM/DD/YYYY'),
                rangeType: 'monthly'
            };
        }
        if (daysBack === 60) {
            return {
                dateBegin: moment().subtract(60, 'd').format('MM/DD/YYYY'),
                dateEnd: moment().format('MM/DD/YYYY'),
                rangeType: 'twoMonths'
            };
        }
        return {
            dateBegin: moment().subtract(90, 'd').format('MM/DD/YYYY'),
            dateEnd: moment().format('MM/DD/YYYY'),
            rangeType: 'quarterly'
        };
    }

    public getColumnSettingsInfo(dynamicColumnsInfo: DynamicListViewColumnData) {
        let formatedResult = this.sampleColumnService.getGridColumnInfo(dynamicColumnsInfo);
        if (!formatedResult) { return null; }
        let columnSettings =  this.sampleColumnService.getColumnSettingInfo(formatedResult.columnHeader, dynamicColumnsInfo);
        columnSettings.subheaderText = 'Report';
        columnSettings.header = 'Custom Report Configuration';
        columnSettings.showReset = true;
        return columnSettings;
    }

    public async showResetDialogConfirmation(info: DynamicListViewColumnData, columnInfo: ColumnSettingInfo, savedFilter: SavedFilter) {
        let data: DeleteModalData = {
            title: 'Confirmation',
            content: 'This will reset columns back to their default order',
            closeButtonText: 'Cancel',
            deleteButtonText: 'Apply',
            reverseButtons: true,
            displayClose: false
        };
        let result = await this.deleteService.showCustomDeleteDialog(data).toPromise();
        if (result && result.isDeleted) {
            return this.resetColumnSettings(info, savedFilter);
        }
        columnInfo.showReset = true;
        return columnInfo;
    }

    public resetColumnSettings(info: DynamicListViewColumnData, savedFilter: SavedFilter) {
        if (!info || !info.dynamicListColumnInfo || info.dynamicListColumnInfo.length === 0) { return null; }
        if (savedFilter && savedFilter.id) {
            this.reportStorage.resetColumnConfig(savedFilter.id);
        }
        return this.getColumnSettingsInfo(info);
    }

    public openEditDialog(columnSettingInfo: ColumnSettingInfo): Observable<ColumnSettingInfo> {
        return this.columnSettingsService.openColumnSettingsPopup(columnSettingInfo).pipe(
            switchMap(dialog => dialog.onHide()),
            map((dialogResult: ColumnSettingInfo) => dialogResult || null)
        );
    }

    public getConfigJsonForColumnInfo(columnSettingInfo: ColumnSettingInfo, columnData: DynamicListViewColumnData) {
        let selectedColumns = columnSettingInfo.selectedColumns.map((sc, index) => {
            let column = columnData.dynamicListColumnInfo.find(t => t.appDataColumnDefinitionId === +sc.key);
            return {
                appDataColumnDefinitionId: column.appDataColumnDefinitionId,
                sequence: index + 1,
            };
        });
        return JSON.stringify(selectedColumns);
    }

    public updateColumnSettingsInfo(columnData: DynamicListViewColumnData, filter: SavedFilter, columnSettings: ColumnSettingInfo) {
        if (!filter.configJSON || !filter.appDataDefinitionId) { return this.getColumnSettingsInfo(columnData); }
        let configInfo: UserConfigInfo[] = JSON.parse(filter.configJSON);
        if (!configInfo || !configInfo.length) { return this.getColumnSettingsInfo(columnData); }
        configInfo = configInfo.sort((a, b) => a.sequence < b.sequence ? -1 : 1);
        columnSettings.selectedColumns = [];
        configInfo.forEach((sc) => {
            let column = columnData.dynamicListColumnInfo.find(item => sc.appDataColumnDefinitionId === item.appDataColumnDefinitionId);
            if (column) {
                columnSettings.selectedColumns.push({
                displayName: column.columnNameDisplay,
                key: column.appDataColumnDefinitionId.toString(),
                sequence: sc.sequence,
                isMandatory: column.mandatoryFlag
                });
            }
        });
        let availableColumns = columnData.dynamicListColumnInfo.filter(item => !configInfo.find(t => t.appDataColumnDefinitionId === item.appDataColumnDefinitionId)
                                                                                                && !item.hiddenFlag);
        columnSettings.availableColumns = availableColumns.map((ac) => {
            return {
                displayName: ac.columnNameDisplay,
                key: ac.appDataColumnDefinitionId.toString(),
                isMandatory: ac.mandatoryFlag
            };
        });
        columnSettings.availableColumns = columnSettings.availableColumns.sort((a, b) => a.displayName.localeCompare(b.displayName));
        return columnSettings;
    }
}